import { useState, useEffect, useRef, startTransition } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Tabs,
    Tab,
    Chip,
    IconButton,
    Alert,
    Skeleton,
    Breadcrumbs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    LinearProgress,
    FormControlLabel,
    FormGroup,
    Tooltip,
    Switch,
    CircularProgress,
    Checkbox,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Delete as DeleteIcon,
    Description as FileIcon,
    Storage as StorageIcon,
    AutoFixHigh as EnrichIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Warning as WarningIcon,
    Search as SearchIcon,
    PlayArrow as PlayIcon,
    CloudUpload as IndexIcon,
    Refresh as ReindexIcon,
    Replay as ReplayIcon,
    FactCheck as ReviewIcon,
    GetApp as DownloadIcon,
    Category as ThemeIcon,
} from '@mui/icons-material';
import {
    getProjectDashboard,
    downloadDataDictionary,
    deleteUploadedFile,
    enrichFileDescriptions,
    classifyFileThemes,
    startPipelineJob,
    resumePipelineJob,
    cancelPipelineJob,
    indexCatalystFile,
    resetIndexingStatus,
} from '../services/api';
import FileUploader from '../components/schema-analyzer/FileUploader';
import TableReviewDialog from '../components/schema-analyzer/TableReviewDialog';

// Data source configuration
const SOURCE_SYSTEMS = [
    { key: 'DFSS', label: 'SOURCE 1', category: 'Source', color: 'primary' },
    { key: 'IDW', label: 'SOURCE 2', category: 'Source', color: 'primary' },
    { key: 'IDDS_L2', label: 'TARGET L2', category: 'Target', color: 'secondary' },
    { key: 'IDDS_L3', label: 'TARGET L3', category: 'Target', color: 'secondary' },
];

/**
 * Project Detail Page
 */
function ProjectDetailPage() {
    const { projectId } = useParams();
    const navigate = useNavigate();

    const [project, setProject] = useState(null);
    const [files, setFiles] = useState({});
    const [enrichmentStatuses, setEnrichmentStatuses] = useState({});
    const [enrichmentProgresses, setEnrichmentProgresses] = useState({});
    const [classificationStatuses, setClassificationStatuses] = useState({});
    const [classificationProgresses, setClassificationProgresses] = useState({});
    const [indexingStatuses, setIndexingStatuses] = useState({});
    const [indexingProgresses, setIndexingProgresses] = useState({});
    const [mappingSummaries, setMappingSummaries] = useState({});
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);
    const [enrichingFile, setEnrichingFile] = useState(null);
    const [classifyingFile, setClassifyingFile] = useState(null);
    const [indexingFile, setIndexingFile] = useState(null);
    const [tableReviewFile, setTableReviewFile] = useState(null);
    const [pipelineJobs, setPipelineJobs] = useState([]);
    const [pipelineJob, setPipelineJob] = useState(null);
    const [pipelineOptions, setPipelineOptions] = useState({
        includeEnrichment: true,
        includeClassification: true,
        includeIndexing: true,
        includeMapping: true,
        forceEnrichment: false,
        forceClassification: false,
        forceIndexing: false,
        forceMapping: false,
        sourceSystems: SOURCE_SYSTEMS.map((system) => system.key),
    });
    const [pipelineActionLoading, setPipelineActionLoading] = useState({
        start: false,
        resume: false,
        cancel: false,
    });
    const previousPipelineRunningRef = useRef(false);
    const isPipelineRunning = pipelineJob && (pipelineJob.status === 'running' || pipelineJob.status === 'queued');
    const isPipelineInterrupted = pipelineJob && pipelineJob.status === 'interrupted';
    const isPipelineCompleted = pipelineJob && pipelineJob.status === 'completed';
    const isPipelineCompletedWithErrors = pipelineJob && pipelineJob.status === 'completed_with_errors';
    const isPipelineCancelled = pipelineJob && pipelineJob.status === 'cancelled';
    const isPipelineTerminal = pipelineJob && (isPipelineCompleted || isPipelineCompletedWithErrors || isPipelineCancelled);
    const hasRunningEnrichmentOperation = Object.values(enrichmentProgresses).some((entry) => entry?.status === 'running');
    const hasRunningClassificationOperation = Object.values(classificationProgresses).some((entry) => entry?.status === 'running');
    const hasRunningIndexingOperation = Object.values(indexingProgresses).some((entry) => entry?.status === 'running');

    useEffect(() => {
        fetchProjectData();
    }, [projectId, refreshKey]);

    const selectActivePipelineJob = (jobs = []) => (
        jobs.find((job) => job.status === 'running' || job.status === 'queued')
        || jobs.find((job) => job.status === 'interrupted')
        || jobs[0]
        || null
    );

    const normalizeOperationStates = (progresses) => {
        const normalizedStates = {};
        Object.entries(progresses || {}).forEach(([fileId, progress]) => {
            const normalized = normalizeOperationState(progress);
            if (normalized) {
                normalizedStates[fileId] = normalized;
            }
        });
        return normalizedStates;
    };

    const normalizeMappingSummary = (summary) => (
        summary?.error
            ? { processed: false, total_columns: 0, pending: 0, accepted: 0, rejected: 0 }
            : summary
    );

    const applyDashboardData = (dashboardData) => {
        const jobList = dashboardData?.pipeline_jobs || [];
        startTransition(() => {
            setProject(dashboardData?.project || null);
            setFiles(dashboardData?.files || {});
            setEnrichmentStatuses(dashboardData?.enrichment_statuses || {});
            setEnrichmentProgresses(normalizeOperationStates(dashboardData?.enrichment_progresses));
            setClassificationStatuses(dashboardData?.classification_statuses || {});
            setClassificationProgresses(normalizeOperationStates(dashboardData?.classification_progresses));
            setIndexingStatuses(dashboardData?.indexing_statuses || {});
            setIndexingProgresses(normalizeOperationStates(dashboardData?.indexing_progresses));
            setMappingSummaries(
                Object.fromEntries(
                    Object.entries(dashboardData?.mapping_summaries || {}).map(([fileId, summary]) => [
                        fileId,
                        normalizeMappingSummary(summary),
                    ])
                )
            );
            setPipelineJobs(jobList);
            setPipelineJob(selectActivePipelineJob(jobList));
        });
    };

    const fetchProjectData = async ({ background = false } = {}) => {
        const isInitialLoad = !project && !background;
        try {
            if (!background && isInitialLoad) {
                setLoading(true);
            } else if (!background) {
                setRefreshing(true);
            }
            const dashboardData = await getProjectDashboard(projectId);
            applyDashboardData(dashboardData);
            setError(null);
        } catch (err) {
            if (err.response?.status === 404) {
                setError('Project not found.');
            } else {
                setError('Failed to load project. Please ensure the backend is running.');
            }
            console.error('Error fetching project:', err);
        } finally {
            if (!background && isInitialLoad) {
                setLoading(false);
            } else if (!background) {
                setRefreshing(false);
            }
        }
    };

    const normalizeOperationState = (operation) => {
        if (!operation || operation.status === 'not_started') {
            return null;
        }

        return {
            status: operation.status,
            message: operation.message,
            error: operation.error,
            progress: {
                completed: operation.completed || 0,
                total: operation.total || 0,
                percent: Math.max(0, Math.min(100, Math.round(operation.progress || 0))),
            },
            stats: operation.stats || null,
        };
    };

    const isFileEnriched = (fileId) => {
        const status = enrichmentStatuses[fileId];
        if (!status) {
            return false;
        }
        return (
            ((status.tables_missing_definition || 0) === 0 && (status.columns_missing_definition || 0) === 0)
            || hasEnrichmentStageCompleted(fileId)
        );
    };

    const hasCompletedStep = (fileId, step) => {
        const state = getPipelineFileState(fileId, step);
        return state?.status === 'completed' || state?.status === 'skipped';
    };

    const hasEnrichmentStageCompleted = (fileId) => {
        const status = enrichmentStatuses[fileId];
        return hasCompletedStep(fileId, 'enrichment') || Boolean(status?.last_enrichment);
    };

    const isFileIndexed = (fileId) => Boolean(indexingStatuses[fileId]?.indexed_to_qdrant);

    const isFileMapped = (fileId) => Boolean(mappingSummaries[fileId]?.processed);

    const isFileClassified = (fileId) => Boolean(classificationStatuses[fileId]?.completed);

    const isFileReadyForProcessing = (file) => Boolean(file?.table_review_can_proceed);
    const isFileReadyForPipeline = (file) => Boolean(file);

    const getSourceFiles = () => SOURCE_SYSTEMS
        .filter((system) => system.category === 'Source')
        .flatMap((system) => files[system.key] || []);

    const getTargetFiles = () => SOURCE_SYSTEMS
        .filter((system) => system.category === 'Target')
        .flatMap((system) => files[system.key] || []);

    const getSystemPendingCount = (system) => {
        const targetReadyForMapping = getTargetFiles().some((file) => isFileReadyForPipeline(file));
        return (files[system.key] || []).reduce((count, file) => {
            if (!isFileReadyForPipeline(file)) {
                return count;
            }
            const needsEnrichment = !isFileEnriched(file.id);
            const needsClassification = !isFileClassified(file.id);
            if (system.category === 'Source') {
                const needsMapping = targetReadyForMapping && !isFileMapped(file.id);
                return count + (needsEnrichment || needsClassification || needsMapping ? 1 : 0);
            }

            const needsIndexing = !isFileIndexed(file.id);
            return count + (needsEnrichment || needsClassification || needsIndexing ? 1 : 0);
        }, 0);
    };

    useEffect(() => {
        const pendingSystemKeys = SOURCE_SYSTEMS
            .filter((system) => getSystemPendingCount(system) > 0)
            .map((system) => system.key);

        const pipelineSourceFiles = getSourceFiles().filter((file) => isFileReadyForPipeline(file));
        const pipelineTargetFiles = getTargetFiles().filter((file) => isFileReadyForPipeline(file));
        const pendingEnrichmentWork = [...pipelineSourceFiles, ...pipelineTargetFiles].some((file) => !isFileEnriched(file.id));
        const pendingClassificationWork = [...pipelineSourceFiles, ...pipelineTargetFiles].some((file) => !isFileClassified(file.id));
        const pendingIndexingWork = pipelineTargetFiles.some((file) => !isFileIndexed(file.id));
        const pendingMappingWork = pipelineTargetFiles.length > 0 && pipelineSourceFiles.some((file) => !isFileMapped(file.id));

        setPipelineOptions((prev) => {
            // Keep the data-source selection aligned with the latest runnable status.
            // When a new file upload introduces fresh pending work, that source system
            // should become enabled automatically instead of staying hidden by the
            // previous selection snapshot.
            const nextSourceSystems = pendingSystemKeys;
            const next = {
                ...prev,
                includeEnrichment: pendingEnrichmentWork ? prev.includeEnrichment : false,
                includeClassification: pendingClassificationWork ? prev.includeClassification : false,
                includeIndexing: pendingIndexingWork ? prev.includeIndexing : false,
                includeMapping: pendingMappingWork ? prev.includeMapping : false,
                sourceSystems: nextSourceSystems,
            };

            if (
                next.includeEnrichment === prev.includeEnrichment
                && next.includeClassification === prev.includeClassification
                && next.includeIndexing === prev.includeIndexing
                && next.includeMapping === prev.includeMapping
                && JSON.stringify(next.sourceSystems) === JSON.stringify(prev.sourceSystems)
            ) {
                return prev;
            }

            return next;
        });
    }, [files, enrichmentStatuses, classificationStatuses, indexingStatuses, mappingSummaries]);

    const handleUploadSuccess = () => {
        setRefreshKey(prev => prev + 1);
    };

    const handleOpenTableReview = (sourceSystem, file) => {
        setTableReviewFile({
            sourceSystem,
            file,
        });
    };

    const handleCloseTableReview = () => {
        setTableReviewFile(null);
    };

    const handleDeleteFile = async (sourceSystem, fileId) => {
        if (!window.confirm('Are you sure you want to delete this file?')) {
            return;
        }

        try {
            await deleteUploadedFile(projectId, sourceSystem, fileId);
            await fetchProjectData({ background: true });
        } catch (err) {
            setError('Failed to delete file. Please try again.');
            console.error('Error deleting file:', err);
        }
    };

    const handleEnrichFile = async (sourceSystem, file) => {
        if (!isFileReadyForProcessing(file)) {
            setError('Complete dropped-table review for this file before enrichment.');
            return;
        }

        try {
            setEnrichingFile(file.id);
            setEnrichmentProgresses((prev) => ({
                ...prev,
                [file.id]: {
                    status: 'running',
                    message: 'Enrichment queued',
                    progress: { completed: 0, total: 0, percent: 0 },
                    error: null,
                    stats: null,
                },
            }));
            await enrichFileDescriptions(projectId, sourceSystem, file.id);
            await fetchProjectData({ background: true });
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Enrichment failed. Please try again.');
            console.error('Error enriching file:', err);
            setEnrichmentProgresses((prev) => {
                const next = { ...prev };
                delete next[file.id];
                return next;
            });
        } finally {
            setEnrichingFile(null);
        }
    };

    const handleDownloadFile = async (sourceSystem, file) => {
        try {
            const { blob, filename } = await downloadDataDictionary(projectId, sourceSystem, file.id);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            const originalName = file?.file_name || 'data_dictionary.xlsx';
            const lastDotIndex = originalName.lastIndexOf('.');
            const baseName = lastDotIndex > 0 ? originalName.slice(0, lastDotIndex) : originalName;
            const extension = lastDotIndex > 0 ? originalName.slice(lastDotIndex) : '.xlsx';
            link.setAttribute('download', filename || `${baseName}_update${extension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to download the latest data dictionary.');
            console.error('Error downloading data dictionary:', err);
        }
    };

    const handleClassifyFile = async (sourceSystem, file) => {
        if (!isFileReadyForProcessing(file)) {
            setError('Complete dropped-table review for this file before theme classification.');
            return;
        }
        if (!isFileEnriched(file.id)) {
            setError('Complete enrichment for this file before theme classification.');
            return;
        }

        try {
            setClassifyingFile(file.id);
            setClassificationProgresses((prev) => ({
                ...prev,
                [file.id]: {
                    status: 'running',
                    message: 'Theme classification queued',
                    progress: { completed: 0, total: 0, percent: 0 },
                    error: null,
                    stats: null,
                },
            }));
            await classifyFileThemes(projectId, sourceSystem, file.id);
            await fetchProjectData({ background: true });
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Theme classification failed. Please try again.');
            console.error('Error classifying file themes:', err);
            setClassificationProgresses((prev) => {
                const next = { ...prev };
                delete next[file.id];
                return next;
            });
        } finally {
            setClassifyingFile(null);
        }
    };

    const handleIndexFile = async (sourceSystem, file) => {
        if (!isFileReadyForProcessing(file)) {
            setError('Complete dropped-table review for this file before indexing.');
            return;
        }
        if (!isFileClassified(file.id)) {
            setError('Complete theme classification for this file before indexing.');
            return;
        }

        try {
            setIndexingFile(file.id);
            setIndexingProgresses((prev) => ({
                ...prev,
                [file.id]: {
                    status: 'running',
                    message: 'Indexing queued',
                    progress: { completed: 0, total: 0, percent: 0 },
                    error: null,
                    stats: null,
                },
            }));
            await indexCatalystFile(projectId, sourceSystem, file.id);
            await fetchProjectData({ background: true });
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Indexing failed. Please try again.');
            console.error('Error indexing file:', err);
            setIndexingProgresses((prev) => {
                const next = { ...prev };
                delete next[file.id];
                return next;
            });
        } finally {
            setIndexingFile(null);
        }
    };

    const setPipelineJobWithJobs = (updatedJob) => {
        if (!updatedJob) {
            return;
        }

        startTransition(() => {
            setPipelineJob(updatedJob);
            setPipelineJobs((prevJobs) => {
                const nextJobs = [...prevJobs];
                const matchIndex = nextJobs.findIndex((job) => job.job_id === updatedJob.job_id);
                if (matchIndex >= 0) {
                    nextJobs[matchIndex] = updatedJob;
                } else {
                    nextJobs.unshift(updatedJob);
                }
                nextJobs.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
                return nextJobs;
            });
        });
    };

    const getPipelineFileState = (fileId, step) => {
        if (!step) {
            return null;
        }
        const fileState = pipelineFileStateById[String(fileId)];
        return fileState?.[step] || null;
    };

    const handleStartPipeline = async () => {
        if (pipelineActionLoading.start || isPipelineRunning) {
            return;
        }

        if (!hasPendingRunnableWork) {
            setError('No pending work is available for the current files.');
            return;
        }

        const effectiveOptions = {
            includeEnrichment: pipelineOptions.includeEnrichment && hasPendingEnrichmentWork,
            includeClassification: pipelineOptions.includeClassification && hasPendingClassificationWork,
            includeIndexing: pipelineOptions.includeIndexing && hasPendingIndexingWork,
            includeMapping: pipelineOptions.includeMapping && hasPendingMappingWork,
        };

        if (!effectiveOptions.includeEnrichment && !effectiveOptions.includeClassification && !effectiveOptions.includeIndexing && !effectiveOptions.includeMapping) {
            setError('Please enable Enrichment, Theme Classification, Indexing, and/or Mapping to start processing.');
            return;
        }

        const selectedSourceSystems = (pipelineOptions.sourceSystems || []).filter(
            (systemKey) => availablePipelineSystems.some((system) => system.key === systemKey)
        );
        const requiredTargetSystems = effectiveOptions.includeMapping
            ? SOURCE_SYSTEMS
                .filter((system) => system.category === 'Target')
                .filter((system) => (files[system.key] || []).some((file) => isFileReadyForPipeline(file)))
                .map((system) => system.key)
            : [];
        const selectedSystemsForJob = Array.from(new Set([...selectedSourceSystems, ...requiredTargetSystems]));

        if (selectedSystemsForJob.length === 0) {
            setError('Select at least one data source with pending work before starting the pipeline.');
            return;
        }

        try {
            setPipelineActionLoading((prev) => ({ ...prev, start: true }));
            const job = await startPipelineJob(projectId, {
                ...pipelineOptions,
                ...effectiveOptions,
                sourceSystems: selectedSystemsForJob,
            });
            setPipelineJobWithJobs(job);
            void fetchProjectData({ background: true });
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to start background processing.');
            console.error('Error starting pipeline:', err);
        } finally {
            setPipelineActionLoading((prev) => ({ ...prev, start: false }));
        }
    };

    const handleResumePipeline = async () => {
        if (!pipelineJob || pipelineActionLoading.resume || isPipelineRunning) {
            return;
        }

        try {
            setPipelineActionLoading((prev) => ({ ...prev, resume: true }));
            const job = await resumePipelineJob(projectId, pipelineJob.job_id);
            setPipelineJobWithJobs(job);
            void fetchProjectData({ background: true });
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to resume pipeline job.');
            console.error('Error resuming pipeline:', err);
        } finally {
            setPipelineActionLoading((prev) => ({ ...prev, resume: false }));
        }
    };

    const handleCancelPipeline = async () => {
        if (!pipelineJob || pipelineActionLoading.cancel || !isPipelineRunning) {
            return;
        }

        try {
            setPipelineActionLoading((prev) => ({ ...prev, cancel: true }));
            const job = await cancelPipelineJob(projectId, pipelineJob.job_id);
            setPipelineJobWithJobs(job);
            void fetchProjectData({ background: true });
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to cancel pipeline job.');
            console.error('Error canceling pipeline:', err);
        } finally {
            setPipelineActionLoading((prev) => ({ ...prev, cancel: false }));
        }
    };

    const updatePipelineOption = (key, value) => {
        setPipelineOptions((prev) => ({ ...prev, [key]: value }));
    };

    const togglePipelineSourceSystem = (systemKey, checked) => {
        setPipelineOptions((prev) => {
            const current = new Set(prev.sourceSystems || []);
            if (checked) {
                current.add(systemKey);
            } else {
                current.delete(systemKey);
            }
            return {
                ...prev,
                sourceSystems: SOURCE_SYSTEMS
                    .map((system) => system.key)
                    .filter((key) => current.has(key)),
            };
        });
    };

    const renderPipelineStepStatus = (file, step, jobStepState, fallbackStatus, type) => {
        if (!isFileReadyForProcessing(file)) {
            return (
                <Tooltip title={`Resolve ${file.table_review_pending || 0} dropped-table review decision(s) before ${type}.`}>
                    <Chip
                        icon={<WarningIcon />}
                        label="Review Pending"
                        size="small"
                        color="warning"
                        variant="outlined"
                    />
                </Tooltip>
            );
        }

        if (
            step === 'mapping'
            && jobStepState?.status === 'completed'
            && !fallbackStatus?.processed
        ) {
            jobStepState = null;
        }

        const shouldPreferFallbackStatus = (() => {
            if (!jobStepState || jobStepState.status === 'running') {
                return false;
            }
            if (step === 'classification') {
                return Boolean(fallbackStatus?.completed);
            }
            if (step === 'indexing') {
                return Boolean(fallbackStatus?.indexed_to_qdrant);
            }
            if (step === 'mapping') {
                return Boolean(fallbackStatus?.processed);
            }
            if (step === 'enrichment') {
                return (
                    (fallbackStatus?.tables_missing_definition || 0) === 0
                    && (fallbackStatus?.columns_missing_definition || 0) === 0
                );
            }
            return false;
        })();

        if (shouldPreferFallbackStatus) {
            jobStepState = null;
        }

        if (!jobStepState && !fallbackStatus) {
            return null;
        }

        const stepVerb = type === 'indexing'
            ? 'Indexing'
            : type === 'mapping'
                ? 'Mapping'
                : type === 'classification'
                    ? 'Classifying'
                    : 'Enriching';
        const stepDoneLabel = type === 'indexing'
            ? 'Indexed'
            : type === 'mapping'
                ? 'Mapped'
                : type === 'classification'
                    ? 'Classified'
                    : 'Enriched';

        const tablesMissing = fallbackStatus?.tables_missing_definition || 0;
        const columnsMissing = fallbackStatus?.columns_missing_definition || 0;
        const isIndexed = fallbackStatus?.indexed_to_qdrant;
        const isClassified = fallbackStatus?.completed;
        const remainingTables = fallbackStatus?.unprocessed_tables || 0;

        const status = jobStepState?.status;
        const percent = jobStepState?.progress?.percent || 0;
        const completed = jobStepState?.progress?.completed || 0;
        const total = jobStepState?.progress?.total || 0;

        if (jobStepState) {
            if (status === 'running') {
                const showProgressBar = total > 0;

                return (
                    <Box sx={{ minWidth: 180 }}>
                        <Typography variant="caption" color="text.secondary">
                            {jobStepState.message || `${stepVerb}...`}
                        </Typography>
                        {showProgressBar ? (
                            <LinearProgress variant="determinate" value={percent} sx={{ mt: 0.5 }} />
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <CircularProgress size={14} />
                                <LinearProgress variant="indeterminate" sx={{ flex: 1 }} />
                            </Box>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            {completed}/{total}
                        </Typography>
                    </Box>
                );
            }

            if (status === 'completed') {
                return (
                    <Tooltip title={jobStepState.message || 'Completed'}>
                        <Chip
                            icon={<CheckIcon />}
                            label={stepDoneLabel}
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                    </Tooltip>
                    );
            }

            if (status === 'skipped') {
                return (
                    <Tooltip title={jobStepState.message || 'Skipped'}>
                        <Chip
                            label="Skipped"
                            size="small"
                            color="default"
                            variant="outlined"
                        />
                    </Tooltip>
                );
            }

            if (status === 'failed') {
                return (
                    <Tooltip title={jobStepState.error || jobStepState.message || 'Failed'}>
                        <Chip
                            icon={<WarningIcon />}
                            label="Failed"
                            size="small"
                            color="error"
                            variant="outlined"
                        />
                    </Tooltip>
                );
            }

            if (status === 'pending' || status === 'not_applicable') {
                return (
                    <Typography variant="caption" color="text.secondary">
                        {status === 'not_applicable' ? 'Not applicable' : 'Pending'}
                    </Typography>
                );
            }
        }

        if (step === 'enrichment') {
            if (enrichingFile === file.id) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption">Enriching...</Typography>
                    </Box>
                );
            }

            if (tablesMissing === 0 && columnsMissing === 0) {
                return (
                    <Tooltip title="All definitions complete">
                        <CheckIcon color="success" fontSize="small" />
                    </Tooltip>
                );
            }

            return (
                <Tooltip title={`${tablesMissing} tables, ${columnsMissing} columns missing definitions`}>
                    <Chip
                        icon={<WarningIcon />}
                        label={`${tablesMissing + columnsMissing} missing`}
                        size="small"
                        color="warning"
                        variant="outlined"
                    />
                </Tooltip>
            );
        }

        if (step === 'mapping') {
            const summary = fallbackStatus || {};
            if (summary.processed) {
                return (
                    <Tooltip title={`Pending: ${summary.pending || 0}, Accepted: ${summary.accepted || 0}, Rejected: ${summary.rejected || 0}`}>
                        <Chip
                            icon={<CheckIcon />}
                            label={`Mapped (${summary.pending || 0} pending)`}
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                    </Tooltip>
                );
            }

            return (
                <Chip
                    label="Not Processed"
                    size="small"
                    color="default"
                    variant="outlined"
                />
            );
        }

        if (step === 'classification') {
            if (classifyingFile === file.id) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption">Classifying...</Typography>
                    </Box>
                );
            }

            if (isClassified) {
                return (
                    <Tooltip title={`Generated: ${fallbackStatus?.generated_tables || 0}, Pending review: ${fallbackStatus?.review_pending || 0}`}>
                        <Chip
                            icon={<CheckIcon />}
                            label="Classified"
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                    </Tooltip>
                );
            }

            return (
                <Tooltip title={`${remainingTables} table(s) still need theme classification`}>
                    <Chip
                        icon={<WarningIcon />}
                        label={`${remainingTables} pending`}
                        size="small"
                        color="warning"
                        variant="outlined"
                    />
                </Tooltip>
            );
        }

        if (indexingFile === file.id) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Indexing...</Typography>
                </Box>
            );
        }

        if (isIndexed) {
            return (
                <Tooltip title={`Indexed on ${new Date(fallbackStatus.indexed_at).toLocaleString()}`}>
                    <Chip
                        icon={<CheckIcon />}
                        label="Indexed"
                        size="small"
                        color="success"
                        variant="outlined"
                    />
                </Tooltip>
            );
        }

        return (
            <Chip
                label="Not Indexed"
                size="small"
                color="default"
                variant="outlined"
            />
        );
    };

    const getActiveSourceSystem = () => SOURCE_SYSTEMS[activeTab];

    useEffect(() => {
        const wasPipelineRunning = previousPipelineRunningRef.current;
        previousPipelineRunningRef.current = Boolean(isPipelineRunning);

        if (!projectId || !pipelineJob?.job_id || isPipelineRunning || !wasPipelineRunning) {
            return;
        }

        void fetchProjectData({ background: true });
    }, [projectId, pipelineJob?.job_id, pipelineJob?.status, isPipelineRunning]);

    useEffect(() => {
        const shouldPollProjectDashboard =
            isPipelineRunning
            || hasRunningEnrichmentOperation
            || hasRunningClassificationOperation
            || hasRunningIndexingOperation;

        if (!projectId || !shouldPollProjectDashboard) {
            return;
        }

        let active = true;
        let pollInFlight = false;
        const poll = async () => {
            if (!active || pollInFlight) {
                return;
            }
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                return;
            }

            pollInFlight = true;
            try {
                await fetchProjectData({ background: true });
            } finally {
                pollInFlight = false;
            }
        };

        poll();
        const timer = setInterval(poll, 2500);
        return () => {
            active = false;
            clearInterval(timer);
        };
    }, [projectId, isPipelineRunning, hasRunningEnrichmentOperation, hasRunningClassificationOperation, hasRunningIndexingOperation]);

    const renderEnrichmentStatus = (file) => {
        const operationState = enrichmentProgresses[file.id] || null;
        return renderPipelineStepStatus(
            file,
            'enrichment',
            getPipelineFileState(file.id, 'enrichment') || operationState,
            enrichmentStatuses[file.id],
            'enrichment'
        );
    };

    const renderClassificationStatus = (file) => {
        const operationState = classificationProgresses[file.id] || null;
        return renderPipelineStepStatus(
            file,
            'classification',
            getPipelineFileState(file.id, 'classification') || operationState,
            classificationStatuses[file.id],
            'classification'
        );
    };

    const renderIndexingStatus = (file) => {
        const operationState = indexingProgresses[file.id] || null;
        return renderPipelineStepStatus(
            file,
            'indexing',
            getPipelineFileState(file.id, 'indexing') || operationState,
            indexingStatuses[file.id],
            'indexing'
        );
    };

    const renderMappingStatus = (file) => {
        return renderPipelineStepStatus(
            file,
            'mapping',
            getPipelineFileState(file.id, 'mapping'),
            mappingSummaries[file.id],
            'mapping'
        );
    };

    const renderTableReviewStatus = (file) => {
        if (file.table_review_total === 0) {
            return (
                <Chip
                    icon={<CheckIcon />}
                    label="No Drops"
                    size="small"
                    color="success"
                    variant="outlined"
                />
            );
        }

        if (file.table_review_can_proceed) {
            return (
                <Tooltip title={`Accepted: ${file.table_review_accepted || 0}, Rejected: ${file.table_review_rejected || 0}`}>
                    <Chip
                        icon={<CheckIcon />}
                        label="Approved"
                        size="small"
                        color="success"
                        variant="outlined"
                    />
                </Tooltip>
            );
        }

        return (
            <Tooltip title={`${file.table_review_pending || 0} dropped-table decision(s) still need review`}>
                <Chip
                    icon={<WarningIcon />}
                    label={`${file.table_review_pending || 0} pending`}
                    size="small"
                    color="warning"
                    variant="outlined"
                />
            </Tooltip>
        );
    };

    const getActiveRunningStepState = () => {
        if (!pipelineJob?.current_step || !pipelineJob.current_file_id) {
            return null;
        }

        const [, stepName] = String(pipelineJob.current_step).split(':');
        if (!stepName) {
            return null;
        }

        return getPipelineFileState(pipelineJob.current_file_id, stepName);
    };

    const pipelineFileStateById = {};
    (pipelineJob?.files || []).forEach((entry) => {
        pipelineFileStateById[String(entry.file_id)] = entry;
    });

    const runningStepState = getActiveRunningStepState();
    const pipelineLogs = (pipelineJob?.logs || []).slice(-60);
    const shouldShowPipelineLogs = Boolean(isPipelineRunning);
    const runningStepPercent = (() => {
        if (runningStepState?.status !== 'running') {
            return null;
        }

        const progressPercent = runningStepState?.progress?.percent;
        const progressTotal = runningStepState?.progress?.total || 0;
        if (typeof progressPercent === 'number' && progressTotal > 0) {
            return Math.max(0, Math.min(100, progressPercent));
        }

        const checkpoint = runningStepState?.checkpoint || {};
        if (
            typeof checkpoint.completed_items === 'number' &&
            typeof checkpoint.total_items === 'number' &&
            checkpoint.total_items > 0
        ) {
            return Math.max(
                0,
                Math.min(100, Math.round((checkpoint.completed_items / checkpoint.total_items) * 100))
            );
        }

        if (
            typeof checkpoint.completed_docs === 'number' &&
            typeof checkpoint.total_docs === 'number' &&
            checkpoint.total_docs > 0
        ) {
            return Math.max(
                0,
                Math.min(100, Math.round((checkpoint.completed_docs / checkpoint.total_docs) * 100))
            );
        }

        if (
            typeof checkpoint.completed_columns === 'number' &&
            typeof checkpoint.total_columns === 'number' &&
            checkpoint.total_columns > 0
        ) {
            return Math.max(
                0,
                Math.min(100, Math.round((checkpoint.completed_columns / checkpoint.total_columns) * 100))
            );
        }

        return null;
    })();
    const runningStepHasProgress = runningStepPercent !== null;

    const completedStepCountFromState = () => {
        if (!pipelineJob?.files) {
            return 0;
        }

        return pipelineJob.files.reduce((count, fileState) => {
            if (pipelineJob?.include_enrichment && ['completed', 'skipped', 'not_applicable'].includes(fileState.enrichment?.status)) {
                count += 1;
            }
            if (pipelineJob?.include_classification && ['completed', 'skipped', 'not_applicable'].includes(fileState.classification?.status)) {
                count += 1;
            }
            if (
                pipelineJob?.include_indexing &&
                fileState.indexing_applicable &&
                ['completed', 'skipped', 'not_applicable'].includes(fileState.indexing?.status)
            ) {
                count += 1;
            }
            if (
                pipelineJob?.include_mapping &&
                fileState.mapping_applicable &&
                ['completed', 'skipped', 'not_applicable'].includes(fileState.mapping?.status)
            ) {
                count += 1;
            }
            return count;
        }, 0);
    };

    const displayedOverallProgress = (() => {
        if (!pipelineJob) {
            return 0;
        }

        const total = pipelineJob.total_steps || 0;
        if (!total) {
            return pipelineJob.progress || 0;
        }

        if (!runningStepHasProgress) {
            return pipelineJob.progress || 0;
        }

        const completedBeforeRunning = completedStepCountFromState();
        const currentPercent = runningStepPercent || 0;
        const normalized = (completedBeforeRunning + (currentPercent / 100)) / total;
        return Math.min(100, Math.max(0, Math.round(normalized * 100)));
    })();

    const hasOverallProgress = displayedOverallProgress > 0;
    const shouldAnimateProgress = isPipelineRunning && !runningStepHasProgress && !hasOverallProgress;
    const progressStatusText = (() => {
        if (!pipelineJob) {
            return '';
        }

        if (runningStepHasProgress) {
            return `${displayedOverallProgress}% complete`;
        }

        if (isPipelineRunning && hasOverallProgress) {
            return `${displayedOverallProgress}% complete`;
        }

        if (pipelineJob.status === 'queued') {
            return 'Pipeline queued';
        }
        if (pipelineJob.status === 'interrupted') {
            return `Pipeline paused at ${displayedOverallProgress}%`;
        }
        if (pipelineJob.status === 'completed') {
            return 'Pipeline completed';
        }
        if (pipelineJob.status === 'completed_with_errors') {
            return 'Pipeline completed with errors';
        }
        if (pipelineJob.status === 'cancelled') {
            return 'Pipeline cancelled';
        }
        if (isPipelineRunning) {
            return 'Pipeline running';
        }
        return `${displayedOverallProgress}% complete`;
    })();
    const pipelineSummaryAlert = (() => {
        if (!pipelineJob || isPipelineRunning) {
            return null;
        }

        if (isPipelineCompleted) {
            return {
                severity: 'success',
                message: pipelineJob.message || 'Completed successfully.',
            };
        }
        if (isPipelineCompletedWithErrors) {
            return {
                severity: 'error',
                message: pipelineJob.last_error || pipelineJob.message || 'Completed with one or more file-level errors.',
            };
        }
        if (isPipelineInterrupted) {
            return {
                severity: 'error',
                message: pipelineJob.last_error || pipelineJob.message || 'Pipeline interrupted before completion.',
            };
        }
        if (isPipelineCancelled) {
            return {
                severity: 'warning',
                message: pipelineJob.message || 'Pipeline cancelled.',
            };
        }
        return null;
    })();
    const formatPipelineLogTime = (timestamp) => {
        if (!timestamp) {
            return '--:--:--';
        }

        const parsed = new Date(timestamp);
        if (Number.isNaN(parsed.getTime())) {
            return timestamp;
        }

        return parsed.toLocaleTimeString();
    };

    if (loading && !project) {
        return (
            <Box>
                <Skeleton variant="text" width={200} height={40} />
                <Skeleton variant="text" width={400} />
                <Skeleton variant="rectangular" height={400} sx={{ mt: 3, borderRadius: 2 }} />
            </Box>
        );
    }

    if (error && !project) {
        return (
            <Box>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/schema-analyzer/projects')}>
                    Back to Projects
                </Button>
            </Box>
        );
    }

    const activeSystem = getActiveSourceSystem();
    const activeFiles = files[activeSystem.key] || [];
    const isTarget = activeSystem.category === 'Target';
    const pendingPipelineSystems = SOURCE_SYSTEMS
        .map((system) => ({
            ...system,
            pendingCount: getSystemPendingCount(system),
        }))
        .filter((system) => system.pendingCount > 0);
    const availablePipelineSystems = pendingPipelineSystems;
    const hasPendingTableReviewWork = [...getSourceFiles(), ...getTargetFiles()].some((file) => !isFileReadyForProcessing(file));
    const pipelineSourceFiles = getSourceFiles().filter((file) => isFileReadyForPipeline(file));
    const pipelineTargetFiles = getTargetFiles().filter((file) => isFileReadyForPipeline(file));
    const hasPendingEnrichmentWork = [...pipelineSourceFiles, ...pipelineTargetFiles].some((file) => !isFileEnriched(file.id));
    const hasPendingClassificationWork = [...pipelineSourceFiles, ...pipelineTargetFiles].some((file) => !isFileClassified(file.id));
    const hasPendingIndexingWork = pipelineTargetFiles.some((file) => !isFileIndexed(file.id));
    const hasPendingMappingWork = pipelineTargetFiles.length > 0 && pipelineSourceFiles.some((file) => !isFileMapped(file.id));
    const hasPendingRunnableWork = hasPendingEnrichmentWork || hasPendingClassificationWork || hasPendingIndexingWork || hasPendingMappingWork;
    const pipelineControlsLocked = isPipelineRunning || pipelineActionLoading.start;
    const canResumePipeline = !!pipelineJob
        && !isPipelineRunning
        && !pipelineActionLoading.resume
        && ['interrupted', 'completed_with_errors', 'cancelled'].includes(pipelineJob.status);

    return (
        <Box>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link to="/schema-analyzer/projects" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Typography color="text.secondary" sx={{ '&:hover': { color: 'primary.main' } }}>
                        Projects
                    </Typography>
                </Link>
                <Typography color="text.primary">{project?.name}</Typography>
            </Breadcrumbs>

            {/* Page Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <IconButton onClick={() => navigate('/schema-analyzer/projects')} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h4" component="h1" fontWeight={700} color="text.primary">
                            {project?.name}
                        </Typography>
                    </Box>
                    {project?.description && (
                        <Typography variant="body1" color="text.secondary" sx={{ ml: 6 }}>
                            {project.description}
                        </Typography>
                    )}
                </Box>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<SearchIcon />}
                    onClick={() => navigate(`/schema-analyzer/projects/${projectId}/mapper`)}
                    disabled={hasPendingTableReviewWork}
                    sx={{ fontWeight: 600 }}
                >
                    Schema Mapper
                </Button>
            </Box>
            {refreshing && <LinearProgress sx={{ mb: 2 }} />}

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {hasPendingTableReviewWork && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    One or more uploaded files have dropped-table decisions waiting for review. Resolve them from the file list before continuing.
                </Alert>
            )}

            {/* Source System Tabs */}
            <Card sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        px: 2,
                    }}
                >
                    {SOURCE_SYSTEMS.map((system, index) => (
                        <Tab
                            key={system.key}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {system.label}
                                    <Chip
                                        label={files[system.key]?.length || 0}
                                        size="small"
                                        color={system.color}
                                        variant="outlined"
                                        sx={{ height: 20, minWidth: 24 }}
                                    />
                                </Box>
                            }
                        />
                    ))}
                </Tabs>

                <CardContent>
                    {/* Category Label */}
                    <Chip
                        label={`${activeSystem.category} System`}
                        color={activeSystem.color}
                        size="small"
                        sx={{ mb: 3 }}
                    />

                    {/* File Uploader */}
                    <FileUploader
                        projectId={projectId}
                        sourceSystem={activeSystem.key}
                        onUploadSuccess={handleUploadSuccess}
                    />

                    {/* Uploaded Files Table */}
                    {activeFiles.length > 0 && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" gutterBottom fontWeight={600} color="text.primary">
                                Uploaded Files ({activeFiles.length})
                            </Typography>
                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                                <Table>
                                    <TableHead sx={{ bgcolor: 'background.default' }}>
                                        <TableRow>
                                            <TableCell>File Name</TableCell>
                                            <TableCell align="center">Tables</TableCell>
                                            <TableCell align="center">Columns</TableCell>
                                            <TableCell align="center">Dropped Tables</TableCell>
                                            <TableCell align="center">Enrichment</TableCell>
                                            <TableCell align="center">Themes</TableCell>
                                            {!isTarget && <TableCell align="center">Mapping</TableCell>}
                                            {isTarget && <TableCell align="center">Vector Index</TableCell>}
                                            <TableCell align="right">Uploaded</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {activeFiles.map((file) => {
                                            const enrichmentStatus = enrichmentStatuses[file.id];
                                            const tablesMissing = enrichmentStatus?.tables_missing_definition || 0;
                                            const columnsMissing = enrichmentStatus?.columns_missing_definition || 0;
                                            const reviewPending = !isFileReadyForProcessing(file);
                                            const needsEnrichment = !reviewPending && !isFileEnriched(file.id);
                                            const classificationStatus = classificationStatuses[file.id];
                                            const isClassified = Boolean(classificationStatus?.completed);
                                            const needsClassification = !reviewPending && !needsEnrichment && !isClassified;

                                            const indexingStatus = indexingStatuses[file.id];
                                            const isIndexed = isTarget && indexingStatus?.indexed_to_qdrant;
                                            const canIndex = isTarget && !reviewPending && isClassified && !indexingStatus?.indexed_to_qdrant;
                                            const showThemeReview = (classificationStatus?.generated_tables || 0) > 0;

                                            return (
                                                <TableRow key={file.id} hover>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <FileIcon fontSize="small" sx={{ color: 'primary.main' }} />
                                                            {file.file_name}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={file.table_count} size="small" color="primary" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={file.column_count} size="small" color="primary" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {renderTableReviewStatus(file)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {renderEnrichmentStatus(file)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {renderClassificationStatus(file)}
                                                    </TableCell>
                                                    {!isTarget && (
                                                        <TableCell align="center">
                                                            {renderMappingStatus(file)}
                                                        </TableCell>
                                                    )}
                                                    {isTarget && (
                                                        <TableCell align="center">
                                                            {renderIndexingStatus(file)}
                                                        </TableCell>
                                                    )}
                                                    <TableCell align="right">
                                                        <Typography variant="body2" color="text.secondary">
                                                            {new Date(file.upload_timestamp).toLocaleString()}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                                            {hasEnrichmentStageCompleted(file.id) && (
                                                                <Tooltip title="View generated descriptions">
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => navigate(`/schema-analyzer/projects/${projectId}/review/${activeSystem.key}/${file.id}`)}
                                                                            disabled={reviewPending}
                                                                            sx={{ color: 'info.main' }}
                                                                        >
                                                                            <ReviewIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                            {showThemeReview && (
                                                                <Tooltip title="Review generated table themes">
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => navigate(`/schema-analyzer/projects/${projectId}/theme-review/${activeSystem.key}/${file.id}`)}
                                                                            disabled={reviewPending || !isClassified}
                                                                            sx={{ color: 'secondary.main' }}
                                                                        >
                                                                            <ThemeIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                            <Tooltip title="Review dropped tables">
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleOpenTableReview(activeSystem.key, file)}
                                                                        sx={{ color: reviewPending ? 'warning.main' : 'success.main' }}
                                                                    >
                                                                        <WarningIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title="Download latest data dictionary">
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDownloadFile(activeSystem.key, file)}
                                                                        sx={{ color: 'primary.main' }}
                                                                    >
                                                                        <DownloadIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            {needsEnrichment && (
                                                                <Tooltip title="Generate missing descriptions using AI">
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleEnrichFile(activeSystem.key, file)}
                                                                            disabled={reviewPending || !!enrichingFile || hasRunningEnrichmentOperation || isPipelineRunning}
                                                                            sx={{ color: 'secondary.main' }}
                                                                        >
                                                                            <EnrichIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                            {needsClassification && (
                                                                <Tooltip title="Classify table themes using AI">
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleClassifyFile(activeSystem.key, file)}
                                                                            disabled={reviewPending || !!classifyingFile || hasRunningClassificationOperation || isPipelineRunning}
                                                                            sx={{ color: 'info.main' }}
                                                                        >
                                                                            <ThemeIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                            {canIndex && (
                                                                <Tooltip title="Index columns to Qdrant for similarity search">
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleIndexFile(activeSystem.key, file)}
                                                                            disabled={reviewPending || !!indexingFile || hasRunningIndexingOperation || isPipelineRunning}
                                                                            sx={{ color: 'info.main' }}
                                                                        >
                                                                            <IndexIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                            {isIndexed && (
                                                                <Tooltip title="Re-index columns (reset status and re-index)">
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={async () => {
                                                                                if (reviewPending) {
                                                                                    setError('Complete dropped-table review for this file before indexing.');
                                                                                    return;
                                                                                }
                                                                                if (!isClassified) {
                                                                                    setError('Complete theme classification for this file before indexing.');
                                                                                    return;
                                                                                }
                                                                                try {
                                                                                    setIndexingFile(file.id);
                                                                                    setIndexingProgresses((prev) => ({
                                                                                        ...prev,
                                                                                        [file.id]: {
                                                                                            status: 'running',
                                                                                            message: 'Indexing queued',
                                                                                            progress: { completed: 0, total: 0, percent: 0 },
                                                                                            error: null,
                                                                                            stats: null,
                                                                                        },
                                                                                    }));
                                                                                    await resetIndexingStatus(projectId, activeSystem.key, file.id);
                                                                                    await indexCatalystFile(projectId, activeSystem.key, file.id);
                                                                                    await fetchProjectData({ background: true });
                                                                                    setError(null);
                                                                                } catch (err) {
                                                                                    setError(err.response?.data?.detail || 'Re-indexing failed.');
                                                                                    setIndexingProgresses((prev) => {
                                                                                        const next = { ...prev };
                                                                                        delete next[file.id];
                                                                                        return next;
                                                                                    });
                                                                                } finally {
                                                                                    setIndexingFile(null);
                                                                                }
                                                                                }}
                                                                            disabled={!!indexingFile || hasRunningIndexingOperation || isPipelineRunning}
                                                                            sx={{ color: 'warning.main' }}
                                                                        >
                                                                            <ReindexIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteFile(activeSystem.key, file.id)}
                                                                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {/* Empty State */}
                    {activeFiles.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4, mt: 2 }}>
                            <StorageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                            <Typography color="text.secondary">
                                No files uploaded for {activeSystem.label} yet.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Upload a data dictionary Excel file above to get started.
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography variant="h6" fontWeight={600} color="text.primary">
                            Background Processing
                        </Typography>
                        <Chip
                            label={pipelineJob ? pipelineJob.status : 'Idle'}
                            color={
                                isPipelineRunning
                                    ? 'primary'
                                    : isPipelineCompleted
                                        ? 'success'
                                        : isPipelineCompletedWithErrors
                                            ? 'error'
                                            : isPipelineInterrupted || isPipelineCancelled
                                                ? 'warning'
                                                : 'default'
                            }
                            variant="outlined"
                            size="small"
                        />
                        {!!pipelineJob?.message && (
                            <Typography variant="caption" color="text.secondary">
                                {pipelineJob.message}
                            </Typography>
                        )}
                        {pipelineJobs.length > 1 && (
                            <Typography variant="caption" color="text.secondary">
                                {pipelineJobs.length} jobs tracked
                            </Typography>
                        )}
                    </Box>

                    {pipelineJob && (
                        <>
                            <LinearProgress
                                variant={shouldAnimateProgress ? 'indeterminate' : 'determinate'}
                                value={shouldAnimateProgress ? undefined : displayedOverallProgress}
                                sx={{ mb: 1, borderRadius: 999, height: 8 }}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {progressStatusText}
                            </Typography>
                            {isPipelineRunning && (
                                <Typography variant="caption" color="text.secondary">
                                    Last check: {new Date(pipelineJob.updated_at).toLocaleTimeString()} | UI heartbeat alive
                                </Typography>
                            )}
                            {isPipelineRunning && (
                                <Typography variant="caption" color="text.secondary">
                                    Current step: {pipelineJob.current_file_name || 'n/a'} ({pipelineJob.current_step || 'n/a'})
                                </Typography>
                            )}
                        </>
                    )}

                    {pipelineSummaryAlert && (
                        <Alert severity={pipelineSummaryAlert.severity} sx={{ mt: 2 }}>
                            {pipelineSummaryAlert.message}
                        </Alert>
                    )}

                    {shouldShowPipelineLogs && (
                        <Paper
                            elevation={0}
                            sx={{
                                mt: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'grey.50',
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight={600}>
                                    Pipeline Logs
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {pipelineLogs.length > 0 ? `${pipelineLogs.length} recent events` : 'Logs appear here while the pipeline runs.'}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    maxHeight: 260,
                                    overflowY: 'auto',
                                    px: 2,
                                    py: 1.5,
                                    bgcolor: '#0b1220',
                                }}
                            >
                                {pipelineLogs.length > 0 ? (
                                    pipelineLogs
                                        .slice()
                                        .reverse()
                                        .map((entry, index) => {
                                            const contextBits = [
                                                entry.source_system,
                                                entry.file_name,
                                                entry.step,
                                                entry.progress_summary,
                                            ].filter(Boolean);

                                            return (
                                                <Box key={`${entry.timestamp || 'log'}-${index}`} sx={{ mb: index === pipelineLogs.length - 1 ? 0 : 1.25 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block',
                                                            color: 'rgba(255,255,255,0.7)',
                                                            fontFamily: 'monospace',
                                                        }}
                                                    >
                                                        [{formatPipelineLogTime(entry.timestamp)}]
                                                        {contextBits.length ? ` ${contextBits.join(' | ')}` : ''}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: entry.level === 'error'
                                                                ? '#ffb4ab'
                                                                : entry.level === 'warning'
                                                                    ? '#fdd663'
                                                                    : '#dbe7ff',
                                                            fontFamily: 'monospace',
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {entry.message}
                                                    </Typography>
                                                </Box>
                                            );
                                        })
                                ) : (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#dbe7ff',
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        No pipeline activity yet.
                                    </Typography>
                                )}
                            </Box>
                        </Paper>
                    )}

                    <Grid container spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={2}>
                            <Button
                                fullWidth
                                startIcon={<PlayIcon />}
                                variant="contained"
                                color="primary"
                                onClick={handleStartPipeline}
                                disabled={!hasPendingRunnableWork || isPipelineRunning || pipelineActionLoading.start}
                            >
                                Start Pipeline
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Button
                                fullWidth
                                startIcon={<ReplayIcon />}
                                variant="outlined"
                                color="warning"
                                onClick={handleResumePipeline}
                                disabled={!canResumePipeline}
                            >
                                Resume
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <Button
                                fullWidth
                                startIcon={<CancelIcon />}
                                variant="outlined"
                                color="error"
                                onClick={handleCancelPipeline}
                                disabled={!pipelineJob || !isPipelineRunning || pipelineActionLoading.cancel}
                            >
                                Cancel
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                                {hasPendingTableReviewWork && (
                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                        Pending dropped-table review items will be auto-applied when the pipeline starts.
                                    </Alert>
                                )}
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    Run options
                                </Typography>
                                <FormGroup row sx={{ gap: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={pipelineOptions.includeEnrichment}
                                                disabled={pipelineControlsLocked || !hasPendingEnrichmentWork}
                                                onChange={(e) => updatePipelineOption('includeEnrichment', e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Enrichment (fill missing definitions)"
                                        labelPlacement="end"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={pipelineOptions.includeClassification}
                                                disabled={pipelineControlsLocked || !hasPendingClassificationWork}
                                                onChange={(e) => updatePipelineOption('includeClassification', e.target.checked)}
                                                color="info"
                                            />
                                        }
                                        label="Theme Classification (tag tables)"
                                        labelPlacement="end"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={pipelineOptions.includeIndexing}
                                                disabled={pipelineControlsLocked || !hasPendingIndexingWork}
                                                onChange={(e) => updatePipelineOption('includeIndexing', e.target.checked)}
                                                color="secondary"
                                            />
                                        }
                                        label="Indexing (vectorize target files)"
                                        labelPlacement="end"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={pipelineOptions.includeMapping}
                                                disabled={pipelineControlsLocked || !hasPendingMappingWork}
                                                onChange={(e) => updatePipelineOption('includeMapping', e.target.checked)}
                                                color="success"
                                            />
                                        }
                                        label="Mapping (source to target matching)"
                                        labelPlacement="end"
                                    />
                                </FormGroup>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                                    Data Sources
                                </Typography>
                                <FormGroup row sx={{ gap: 1 }}>
                                    {availablePipelineSystems.map((system) => (
                                        <FormControlLabel
                                            key={system.key}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={(pipelineOptions.sourceSystems || []).includes(system.key)}
                                                    disabled={pipelineControlsLocked}
                                                    onChange={(e) => togglePipelineSourceSystem(system.key, e.target.checked)}
                                                />
                                            }
                                            label={`${system.label} (${system.pendingCount})`}
                                        />
                                    ))}
                                </FormGroup>
                                {availablePipelineSystems.length === 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                        No data sources currently have runnable pending work.
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    Only data sources with pending runnable work are shown here.
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <TableReviewDialog
                open={Boolean(tableReviewFile)}
                projectId={projectId}
                sourceSystem={tableReviewFile?.sourceSystem || null}
                file={tableReviewFile?.file || null}
                onClose={handleCloseTableReview}
                onUpdated={handleUploadSuccess}
            />
        </Box>
    );
}

export default ProjectDetailPage;
